/**
 * snippetItems — pure helpers for the Present-complaints ITEM picker
 * (`ComplaintItemsField.jsx`, DISCHARGE-TEMPLATES 1b Slice 1 · COMP-FE).
 *
 * `splitComplaintItems` is a BYTE-FOR-BYTE re-implementation of the
 * backend's `split_complaint_items` (COMP-BE,
 * `app/services/discharge_snippet_learn.py`, ruling K's five rules,
 * build-plan-1b-slice1-COMP.md §1.4) — same split order, same bullet strip,
 * same trim/drop-empty, same case-insensitive dedupe (first-seen casing
 * wins), same 120-char cap. It is the LEARNABLE view only — "would the
 * server learn/suggest this piece" — and is used ONLY for suggestion-dedupe
 * / "already added" identity checks, NEVER to build the field's stored
 * value (bk-1295, B1). `splitStoredItems` below is the LOSSLESS twin that
 * owns the stored-value round trip: mount-time parse and every
 * add/remove/commit go through IT, so a legacy over-120 or duplicate piece
 * is never silently dropped from a statutory discharge document.
 *
 * Shape modelled on `src/utils/rxMemory.js` (normalise / dedupe / row-filter
 * helpers) for consistency of style ONLY. **DO NOT import `rxMemory.js`** —
 * it carries OPD-ratified copy and OPD wire types onto an IPD screen
 * (build-plan-1b.md §A.3, decided: chief_complaints is NOT seeded from OPD
 * Rx C/O memory). This module is independently re-implemented, never
 * derived from OPD's.
 *
 * Pure module — no React, no API, no store, no fetch.
 */

// COMP-BE's `SNIPPET_ITEM_MAX_LENGTH` (`app/core/discharge_field_catalogue.py`)
// — mirrored here as the ONE cap this module and `ComplaintItemsField.jsx`
// both read, never a second literal `120`.
export const SNIPPET_ITEM_MAX_LENGTH = 120;

// bk-1305 fix — the backend's `_TRAILING` set (`app/core/rx_memory_types.py`,
// `normalise_memory_key`), mirrored here as the ONE trailing-separator set
// this module reads, never a second literal.
const _TRAILING_RE = /[ \t,;.:]+$/;

/**
 * Trim + collapse whitespace + drop trailing separators + casefold — the FE
 * mirror of the backend's imported `normalise_memory_key`
 * (`app/core/rx_memory_types.py:34`). Used ONLY for case-insensitive
 * identity (dedupe against existing chips / "is this suggestion already a
 * chip"), never for display — the chip and the suggestion row both render
 * the item's own first-seen casing verbatim.
 *
 * bk-1305 — this used to omit the backend's trailing-separator strip
 * (`" \t,;.:"`), so "fever." and "Fever" keyed as two different chips'
 * worth of "different" here while the backend learned them as ONE row.
 * `_TRAILING_RE` closes that gap; the STORED value is untouched
 * (`splitStoredItems` never calls this).
 */
export function normaliseItemKey(text) {
  return String(text == null ? '' : text).trim().replace(/\s+/g, ' ').replace(_TRAILING_RE, '').toLowerCase();
}

/**
 * splitComplaintItems — ruling K's five rules, byte-for-byte the backend's
 * `split_complaint_items` (§1.4):
 *   1. split on newline AND comma, both, in one pass;
 *   2. strip a leading "- " bullet marker from each piece;
 *   3. `trim()` each piece, drop empties;
 *   4. dedupe case-insensitively (`normaliseItemKey`) — first-seen casing
 *      wins;
 *   5. skip any piece over `SNIPPET_ITEM_MAX_LENGTH` (120) chars — it is a
 *      sentence, not a complaint. Never truncated.
 *
 * Returns a plain array of strings (never a wrapper object) — every stored
 * value (mount-time parse, §1.5) and every typed/pasted batch
 * (`ComplaintItemsField`'s commit-on-Enter/comma) goes through this ONE
 * function, so "what the chips show" and "what the backend would learn at
 * save" can never drift.
 */
export function splitComplaintItems(text) {
  const raw = String(text == null ? '' : text);
  const pieces = raw
    .split(/[\n,]/)
    .map((piece) => piece.replace(/^-\s+/, '').trim())
    .filter(Boolean);
  const seen = new Set();
  const out = [];
  pieces.forEach((piece) => {
    if (piece.length > SNIPPET_ITEM_MAX_LENGTH) return;
    const key = normaliseItemKey(piece);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(piece);
  });
  return out;
}

/**
 * itemsToStoredValue — the FROZEN §1.5 wire shape: one `"- item"` line per
 * chip (`"- fever\n- cough\n- cold"`). This is the ONLY place a chip list is
 * turned back into the field's stored string — `ComplaintItemsField` never
 * builds this shape by hand at a second call site.
 */
export function itemsToStoredValue(items) {
  return (items || []).map((item) => `- ${item}`).join('\n');
}

/**
 * splitStoredItems — bk-1295 (B1, BLOCKER) fix. The LOSSLESS twin of
 * `splitComplaintItems`: splits the field's STORED value on newline+comma,
 * strips a leading `"- "` bullet marker, trims each piece, and drops ONLY
 * truly empty pieces. Unlike `splitComplaintItems` (the LEARNABLE view,
 * ruling K — caps at `SNIPPET_ITEM_MAX_LENGTH`, dedupes case-insensitively)
 * this function NEVER drops an over-120 piece and NEVER dedupes — every
 * piece the stored string carries, including a legacy duplicate or an
 * over-long clause, comes back out exactly once, in order.
 *
 * This is the ONE function `ComplaintItemsField` uses to derive its chips
 * from `value` and to read "what's already there" before building the next
 * `itemsToStoredValue(...)` call — the caps/dedupe rules that exist for
 * LEARNING/SUGGESTING (i.e. `splitComplaintItems`) never apply to what is
 * stored. A round trip through mount → parse → add/remove one chip →
 * `itemsToStoredValue` therefore preserves every other original piece
 * byte-for-byte (order kept) — the guarantee `splitComplaintItems` as a
 * write path silently broke (bk-1295).
 */
export function splitStoredItems(text) {
  const raw = String(text == null ? '' : text);
  return raw
    .split(/[\n,]/)
    .map((piece) => piece.replace(/^-\s+/, '').trim())
    .filter(Boolean);
}

/**
 * filterItemRows — the long-list search filter for the remembered-items
 * popover (standing DoD #4, same case-insensitive-substring rule
 * `SnippetPicker.jsx`'s own search already uses). A row is a §2.4/§1.2
 * 7-key snippet entry; `title` and `text` are equal by construction on an
 * item row (§1.2 — "an item has no name distinct from itself"), so matching
 * on `text` alone is sufficient and matches what the FE actually renders.
 */
export function filterItemRows(rows, query) {
  const q = normaliseItemKey(query);
  if (!q) return rows;
  return (rows || []).filter((row) => normaliseItemKey((row && row.text) || '').includes(q));
}
